import api from './api'
import { currentWorkerServiceWorker, registerWorkerServiceWorker } from './workerPwa'

/**
 * Browser push subscription lifecycle.
 *
 * The VAPID PUBLIC key is fetched from the backend; the private signing key
 * never reaches the browser. Subscribing/unsubscribing keeps the browser's own
 * PushSubscription and the server record in step, and an endpoint the browser
 * has already discarded is cleaned up server-side rather than left to fail
 * forever.
 */

export interface WebPushConfig {
  configured: boolean
  entitled: boolean
  publicKey: string | null
}

export type WebPushSubscriptionState =
  | 'subscribed'
  | 'not_subscribed'
  | 'unsupported'
  | 'permission_denied'
  | 'not_configured'
  | 'not_entitled'

const urlBase64ToUint8Array = (base64: string): ArrayBuffer => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalized)
  const output = new Uint8Array(raw.length)
  for (let index = 0; index < raw.length; index += 1) output[index] = raw.charCodeAt(index)
  return output.buffer
}

const arrayBufferToBase64Url = (buffer: ArrayBuffer | null): string => {
  if (!buffer) return ''
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export async function fetchWebPushConfig(): Promise<WebPushConfig> {
  try {
    const response = await api.get('/api/notifications/web-push/config')
    return {
      configured: Boolean(response.data?.configured),
      entitled: Boolean(response.data?.entitled),
      publicKey: response.data?.publicKey || null,
    }
  } catch {
    return { configured: false, entitled: false, publicKey: null }
  }
}

export async function currentPushSubscription(): Promise<PushSubscription | null> {
  const registration = await currentWorkerServiceWorker()
  if (!registration?.pushManager) return null
  try {
    return await registration.pushManager.getSubscription()
  } catch {
    return null
  }
}

export async function readSubscriptionState(config?: WebPushConfig): Promise<WebPushSubscriptionState> {
  if (typeof window === 'undefined' || !('PushManager' in window) || !('Notification' in window)) return 'unsupported'
  const resolved = config || await fetchWebPushConfig()
  if (!resolved.entitled) return 'not_entitled'
  if (!resolved.configured) return 'not_configured'
  if ((window as any).Notification.permission === 'denied') return 'permission_denied'
  return (await currentPushSubscription()) ? 'subscribed' : 'not_subscribed'
}

/**
 * Subscribe this browser. Requires an explicit worker action — the permission
 * prompt is a one-shot resource and must never be spent on page load.
 */
export async function subscribeToWebPush(config?: WebPushConfig): Promise<{ state: WebPushSubscriptionState; message?: string }> {
  if (typeof window === 'undefined' || !('PushManager' in window) || !('Notification' in window)) {
    return { state: 'unsupported' }
  }
  const resolved = config || await fetchWebPushConfig()
  if (!resolved.entitled) return { state: 'not_entitled' }
  if (!resolved.configured || !resolved.publicKey) return { state: 'not_configured' }

  const permission = await (window as any).Notification.requestPermission()
  if (permission !== 'granted') return { state: 'permission_denied' }

  const registration = (await currentWorkerServiceWorker()) || (await registerWorkerServiceWorker())
  if (!registration?.pushManager) return { state: 'unsupported' }

  try {
    const existing = await registration.pushManager.getSubscription()
    const subscription = existing || await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(resolved.publicKey),
    })
    await api.post('/api/notifications/web-push/subscriptions', {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: arrayBufferToBase64Url(subscription.getKey('p256dh')),
        auth: arrayBufferToBase64Url(subscription.getKey('auth')),
      },
      applicationSurface: 'worker_web',
      displayMode: window.matchMedia?.('(display-mode: standalone)')?.matches ? 'standalone' : 'browser',
      locale: typeof document !== 'undefined' ? document.documentElement.lang || undefined : undefined,
    })
    return { state: 'subscribed' }
  } catch (error) {
    return {
      state: 'not_subscribed',
      message: (error as any)?.response?.data?.message || (error as Error)?.message,
    }
  }
}

export async function unsubscribeFromWebPush(): Promise<boolean> {
  const subscription = await currentPushSubscription()
  if (!subscription) return true
  const endpoint = subscription.endpoint
  let removedLocally = false
  try {
    removedLocally = await subscription.unsubscribe()
  } catch {
    removedLocally = false
  }
  try {
    await api.delete('/api/notifications/web-push/subscriptions', { data: { endpoint } })
  } catch {
    // The browser subscription is already gone; a failed server cleanup is
    // resolved by the 404/410 pruning on the next delivery attempt.
  }
  return removedLocally
}
